import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm, useFieldArray } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  useListInventoryVendors,
  useGetInventoryVendor,
  useCreateInventoryVendor,
  useUpdateInventoryVendor,
  useDeleteInventoryVendor,
  useCreateVendorContact,
  useDeleteVendorContact,
  useListSpaces,
  getListInventoryVendorsQueryKey,
  getGetInventoryVendorQueryKey,
  BusinessType,
  VendorType,
  VendorStatus,
} from "@workspace/api-client-react";
import type { InventoryVendor } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { venId } from "@/lib/display-id";

const VENDOR_STATUS_COLORS: Record<string, string> = {
  prospect: "",
  onboarding: "bg-blue-100 text-blue-700",
  active: "bg-green-100 text-green-700",
  on_hold: "bg-amber-100 text-amber-700",
  blacklisted: "bg-red-100 text-red-700",
};

/**
 * Three-button Online/Offline/Service toggle for vendor nature. Reads/writes
 * the same form field as the detailed Vendor Type dropdown — they stay in
 * sync. Clicking a button sets the canonical default for that nature:
 *   Online   -> Quick_Commerce        (Amazon, Zepto, Blinkit, …)
 *   Offline  -> Regular               (local kirana, bulk vendor)
 *   Service  -> Service               (CA, plumber, …)
 * The detailed dropdown stays available for finer choices
 * (Online_Marketplace, One_Time).
 */
function VendorNatureToggle({ value, onChange }: { value: string | undefined; onChange: (v: string) => void }) {
  const nature: "Online" | "Offline" | "Service" | null = (() => {
    if (!value) return null;
    if (value === "Quick_Commerce" || value === "Online_Marketplace") return "Online";
    if (value === "Regular" || value === "One_Time") return "Offline";
    if (value === "Service") return "Service";
    return null;
  })();
  const options: Array<{ nature: "Online" | "Offline" | "Service"; defaultType: string; cls: string }> = [
    { nature: "Online",  defaultType: "Quick_Commerce", cls: "bg-sky-100 text-sky-800 border-sky-300" },
    { nature: "Offline", defaultType: "Regular",        cls: "bg-slate-100 text-slate-800 border-slate-300" },
    { nature: "Service", defaultType: "Service",        cls: "bg-purple-100 text-purple-800 border-purple-300" },
  ];
  return (
    <div className="mt-1 inline-flex rounded-md border bg-card p-0.5">
      {options.map((o) => {
        const active = nature === o.nature;
        return (
          <button
            key={o.nature}
            type="button"
            onClick={() => onChange(o.defaultType)}
            className={`px-3 h-7 text-xs font-medium rounded-sm transition-colors ${active ? o.cls + " border" : "text-muted-foreground hover:bg-muted/60"}`}
            aria-pressed={active}
          >
            {o.nature}
          </button>
        );
      })}
    </div>
  );
}

const VendorCreateSchema = z.object({
  vendorName: z.string().min(1, "Vendor name is required"),
  businessType: z.string().min(1, "Business type is required"),
  vendorType: z.string().min(1, "Vendor type is required"),
  gstin: z.string().nullable().optional(),
  pan: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  officeAddress: z.string().nullable().optional(),
  billingAddress: z.string().nullable().optional(),
  pickupAddress: z.string().nullable().optional(),
  pinCode: z.string().nullable().optional(),
  bankName: z.string().nullable().optional(),
  accountHolder: z.string().nullable().optional(),
  accountNumber: z.string().nullable().optional(),
  ifsc: z.string().nullable().optional(),
  upiId: z.string().nullable().optional(),
  cancelledChequeOnFile: z.boolean().nullable().optional(),
  defaultPaymentTerms: z.string().nullable().optional(),
  tdsApplicable: z.boolean().nullable().optional(),
  tdsRate: z.number().nullable().optional(),
  preferredPaymentMode: z.string().nullable().optional(),
  serviceArea: z.string().nullable().optional(),
  standardLeadTimeDays: z.number().nullable().optional(),
  minOrderValue: z.number().nullable().optional(),
  returnPolicy: z.string().nullable().optional(),
  emergencyContactAvailable: z.boolean().nullable().optional(),
  operatingHours: z.string().nullable().optional(),
  status: z.string().optional(),
  spaceIds: z.array(z.number()).min(1, "At least one space required"),
  primarySpaceId: z.number({ required_error: "Primary space is required" }),
  contacts: z.array(z.object({
    role: z.string(),
    name: z.string(),
    designation: z.string().nullable().optional(),
    mobile: z.string().nullable().optional(),
    whatsapp: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    isPrimary: z.boolean().nullable().optional(),
  })).optional(),
}).superRefine((data, ctx) => {
  if (!data.spaceIds.includes(data.primarySpaceId)) {
    ctx.addIssue({ code: "custom", path: ["primarySpaceId"], message: "Primary space must be one of the selected spaces" });
  }
});

type VendorFormValues = z.infer<typeof VendorCreateSchema>;

interface Props {
  openCreate: boolean;
  setOpenCreate: (open: boolean) => void;
}

function VendorSheetContent({ vendorId, onClose }: { vendorId: number | null; onClose: () => void }) {
  const { data: vendorDetail, isLoading } = useGetInventoryVendor(vendorId ?? 0, {
    query: { enabled: vendorId !== null, queryKey: getGetInventoryVendorQueryKey(vendorId ?? 0) }
  });
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const updateMutation = useUpdateInventoryVendor();
  const createContactMutation = useCreateVendorContact();
  const deleteContactMutation = useDeleteVendorContact();

  const [newContact, setNewContact] = useState({ name: "", role: "primary", mobile: "" });

  if (isLoading || !vendorDetail) {
    return <div className="p-6"><Skeleton className="h-96 w-full" /></div>;
  }

  async function handleArchive() {
    if (!vendorDetail) return;
    try {
      await updateMutation.mutateAsync({ id: vendorDetail.id, data: { status: "archived" } });
      toast({ title: "Vendor archived." });
      queryClient.invalidateQueries({ queryKey: getListInventoryVendorsQueryKey() });
      onClose();
    } catch {
      toast({ title: "Something went wrong. Please try again.", variant: "destructive" });
    }
  }

  // 2026-05-20: inline status change (e.g. prospect → active) from the detail panel.
  async function handleStatusChange(next: string) {
    if (!vendorDetail || next === vendorDetail.status) return;
    try {
      await updateMutation.mutateAsync({
        id: vendorDetail.id,
        data: { status: next as (typeof VendorStatus)[keyof typeof VendorStatus] },
      });
      toast({ title: `Status changed to ${next.replace(/_/g, " ")}.` });
      queryClient.invalidateQueries({ queryKey: getGetInventoryVendorQueryKey(vendorDetail.id) });
      queryClient.invalidateQueries({ queryKey: getListInventoryVendorsQueryKey() });
    } catch {
      toast({ title: "Status change failed. Please try again.", variant: "destructive" });
    }
  }

  async function handleAddContact() {
    if (!newContact.name.trim() || !vendorDetail) return;
    try {
      await createContactMutation.mutateAsync({
        vendorId: vendorDetail.id,
        data: {
          role: newContact.role as "primary" | "secondary" | "escalation" | "other",
          name: newContact.name.trim(),
          mobile: newContact.mobile || null,
        }
      });
      toast({ title: "Contact added." });
      queryClient.invalidateQueries({ queryKey: getGetInventoryVendorQueryKey(vendorDetail.id) });
      setNewContact({ name: "", role: "primary", mobile: "" });
    } catch {
      toast({ title: "Something went wrong. Please try again.", variant: "destructive" });
    }
  }

  async function handleRemoveContact(contactId: number) {
    if (!vendorDetail) return;
    try {
      await deleteContactMutation.mutateAsync({ vendorId: vendorDetail.id, id: contactId });
      toast({ title: "Contact removed." });
      queryClient.invalidateQueries({ queryKey: getGetInventoryVendorQueryKey(vendorDetail.id) });
    } catch {
      toast({ title: "Something went wrong. Please try again.", variant: "destructive" });
    }
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="p-6 space-y-4">
            <div>
              <p className="text-sm font-semibold text-foreground mb-3">Identity & Legal</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Vendor Name:</span> <span className="font-medium">{vendorDetail.vendorName}</span></div>
                <div><span className="text-muted-foreground">Type:</span> <span>{vendorDetail.vendorType}</span></div>
                <div><span className="text-muted-foreground">Business Type:</span> <span>{vendorDetail.businessType}</span></div>
                <div><span className="text-muted-foreground">GSTIN:</span> <span>{vendorDetail.gstin ?? "—"}</span></div>
                <div><span className="text-muted-foreground">PAN:</span> <span>{vendorDetail.pan ?? "—"}</span></div>
              </div>
            </div>
            <Separator className="my-4" />
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-foreground">Contacts</p>
              </div>
              {vendorDetail.contacts?.map((contact) => (
                <div key={contact.id} className="flex items-center justify-between text-sm py-1 border-b last:border-0">
                  <div>
                    <span className="font-medium">{contact.name}</span>
                    <span className="text-muted-foreground ml-2">({contact.role})</span>
                    {contact.mobile && <span className="text-muted-foreground ml-2">{contact.mobile}</span>}
                  </div>
                  <Button size="sm" variant="ghost" className="text-destructive text-xs"
                    onClick={() => handleRemoveContact(contact.id)}>Remove</Button>
                </div>
              ))}
              <div className="mt-3 grid grid-cols-3 gap-2">
                <Input placeholder="Name" value={newContact.name} onChange={(e) => setNewContact(p => ({ ...p, name: e.target.value }))} />
                <Input placeholder="Mobile" value={newContact.mobile} onChange={(e) => setNewContact(p => ({ ...p, mobile: e.target.value }))} />
                <Button size="sm" variant="outline" onClick={handleAddContact}>+ Add Contact</Button>
              </div>
            </div>
            <Separator className="my-4" />
            <div>
              <p className="text-sm font-semibold text-foreground mb-3">Addresses</p>
              <div className="text-sm space-y-1">
                <div><span className="text-muted-foreground">City:</span> {vendorDetail.city ?? "—"}, {vendorDetail.state ?? "—"}</div>
                <div><span className="text-muted-foreground">Office:</span> {vendorDetail.officeAddress ?? "—"}</div>
              </div>
            </div>
            <Separator className="my-4" />
            <div>
              <p className="text-sm font-semibold text-foreground mb-3">Status</p>
              <div className="flex items-center gap-3">
                <Badge className={VENDOR_STATUS_COLORS[vendorDetail.status] ?? ""} variant={vendorDetail.status === "archived" ? "outline" : "secondary"}>
                  {vendorDetail.status}
                </Badge>
                <Select value={vendorDetail.status} onValueChange={handleStatusChange}>
                  <SelectTrigger className="w-[200px] h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.values(VendorStatus).map((s) => (
                      <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">Change status here — e.g. promote a prospect to active.</p>
            </div>
          </div>
        </ScrollArea>
      </div>
      <SheetFooter className="p-6 border-t">
        <Button variant="outline" onClick={onClose}>Close</Button>
        {vendorDetail.status !== "archived" && (
          <Button variant="destructive" onClick={handleArchive}>
            Archive Vendor
          </Button>
        )}
      </SheetFooter>
    </div>
  );
}

export function VendorsTab({ openCreate, setOpenCreate }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [viewVendorId, setViewVendorId] = useState<number | null>(null);
  const [archiveVendor, setArchiveVendor] = useState<InventoryVendor | null>(null);
  // 2026-05-22: edit (reuse the create form) + hard delete actions.
  const [editVendor, setEditVendor] = useState<InventoryVendor | null>(null);
  const [deleteVendor, setDeleteVendor] = useState<InventoryVendor | null>(null);
  const [filterStatus, setFilterStatus] = useState<string | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState("");

  const { data: vendors, isLoading } = useListInventoryVendors(
    { status: filterStatus as (typeof VendorStatus)[keyof typeof VendorStatus] | undefined, search: searchQuery || undefined }
  );
  const { data: spaces } = useListSpaces();
  const createMutation = useCreateInventoryVendor();
  const updateMutation = useUpdateInventoryVendor();
  const deleteMutation = useDeleteInventoryVendor();

  const form = useForm<VendorFormValues>({
    resolver: zodResolver(VendorCreateSchema),
    defaultValues: {
      vendorName: "",
      businessType: "Proprietorship",
      vendorType: "Regular",
      status: "prospect",
      cancelledChequeOnFile: false,
      tdsApplicable: false,
      emergencyContactAvailable: false,
      spaceIds: [],
      primarySpaceId: 1,
      contacts: [],
    },
  });

  const { fields: contactFields, append: appendContact, remove: removeContact } = useFieldArray({
    control: form.control,
    name: "contacts",
  });

  // Single-space orgs: picker is hidden, so we auto-fill spaceIds + primarySpaceId
  // here so the Zod `spaceIds.min(1)` + superRefine pass. Without this, Save Vendor
  // submits silently fail (no visible error since the field UI isn't rendered).
  useEffect(() => {
    if (!openCreate) return;
    if (!spaces || spaces.length !== 1) return;
    const only = spaces[0]!.id;
    if ((form.getValues("spaceIds") ?? []).length === 0) {
      form.setValue("spaceIds", [only], { shouldValidate: false });
    }
    form.setValue("primarySpaceId", only, { shouldValidate: false });
  }, [openCreate, spaces, form]);

  const isSheetOpen = openCreate || viewVendorId !== null;

  function handleClose() {
    setOpenCreate(false);
    setViewVendorId(null);
    setEditVendor(null);
    form.reset();
  }

  // 2026-05-22: open the same form pre-filled to edit an existing vendor's details.
  function openEditVendor(vendor: InventoryVendor) {
    setViewVendorId(null);
    setOpenCreate(false);
    setEditVendor(vendor);
    form.reset({
      vendorName: vendor.vendorName,
      businessType: vendor.businessType,
      vendorType: vendor.vendorType,
      gstin: vendor.gstin ?? null,
      pan: vendor.pan ?? null,
      city: vendor.city ?? null,
      state: vendor.state ?? null,
      officeAddress: vendor.officeAddress ?? null,
      billingAddress: vendor.billingAddress ?? null,
      pickupAddress: vendor.pickupAddress ?? null,
      pinCode: vendor.pinCode ?? null,
      bankName: vendor.bankName ?? null,
      accountHolder: vendor.accountHolder ?? null,
      accountNumber: vendor.accountNumber ?? null,
      ifsc: vendor.ifsc ?? null,
      upiId: vendor.upiId ?? null,
      cancelledChequeOnFile: vendor.cancelledChequeOnFile,
      defaultPaymentTerms: vendor.defaultPaymentTerms ?? null,
      tdsApplicable: vendor.tdsApplicable,
      tdsRate: vendor.tdsRate ?? null,
      preferredPaymentMode: vendor.preferredPaymentMode ?? null,
      serviceArea: vendor.serviceArea ?? null,
      standardLeadTimeDays: vendor.standardLeadTimeDays ?? null,
      minOrderValue: vendor.minOrderValue ?? null,
      returnPolicy: vendor.returnPolicy ?? null,
      emergencyContactAvailable: vendor.emergencyContactAvailable,
      operatingHours: vendor.operatingHours ?? null,
      status: vendor.status,
      spaceIds: vendor.spaces?.length ? vendor.spaces : [vendor.spaceId],
      primarySpaceId: vendor.primarySpaceId ?? vendor.spaceId,
      contacts: [],
    });
  }

  async function handleDeleteConfirm() {
    if (!deleteVendor) return;
    try {
      await deleteMutation.mutateAsync({ id: deleteVendor.id });
      toast({ title: `${venId(deleteVendor.id)} deleted.` });
      queryClient.invalidateQueries({ queryKey: getListInventoryVendorsQueryKey() });
      setDeleteVendor(null);
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      // A vendor referenced by stock-ins / pricing can't be hard-deleted — guide to Archive.
      if (status === 409 || status === 400 || status === 500) {
        toast({
          title: "Can't delete — vendor has linked records (stock-ins / pricing). Archive it instead.",
          variant: "destructive",
        });
      } else {
        toast({ title: "Something went wrong. Please try again.", variant: "destructive" });
      }
      setDeleteVendor(null);
    }
  }

  async function onSubmit(values: VendorFormValues) {
    try {
      // Edit mode: PATCH the editable core fields (spaces/contacts managed elsewhere).
      if (editVendor) {
        await updateMutation.mutateAsync({
          id: editVendor.id,
          data: {
            vendorName: values.vendorName,
            businessType: values.businessType as typeof BusinessType[keyof typeof BusinessType],
            vendorType: values.vendorType as typeof VendorType[keyof typeof VendorType],
            gstin: values.gstin,
            pan: values.pan,
            city: values.city,
            state: values.state,
            officeAddress: values.officeAddress,
            billingAddress: values.billingAddress,
            pickupAddress: values.pickupAddress,
            pinCode: values.pinCode,
            bankName: values.bankName,
            accountHolder: values.accountHolder,
            accountNumber: values.accountNumber,
            ifsc: values.ifsc,
            upiId: values.upiId,
            cancelledChequeOnFile: values.cancelledChequeOnFile,
            defaultPaymentTerms: values.defaultPaymentTerms,
            tdsApplicable: values.tdsApplicable,
            tdsRate: values.tdsRate,
            preferredPaymentMode: values.preferredPaymentMode,
            serviceArea: values.serviceArea,
            standardLeadTimeDays: values.standardLeadTimeDays,
            minOrderValue: values.minOrderValue,
            returnPolicy: values.returnPolicy,
            emergencyContactAvailable: values.emergencyContactAvailable,
            operatingHours: values.operatingHours,
            status: values.status as typeof VendorStatus[keyof typeof VendorStatus] | undefined,
          },
        });
        toast({ title: "Vendor updated." });
        queryClient.invalidateQueries({ queryKey: getListInventoryVendorsQueryKey() });
        queryClient.invalidateQueries({ queryKey: getGetInventoryVendorQueryKey(editVendor.id) });
        handleClose();
        return;
      }
      // 1-space environments (Hoblix today): picker is hidden; auto-fill from the single space row
      const finalSpaceIds = values.spaceIds.length > 0
        ? values.spaceIds
        : (spaces?.length === 1 ? [spaces[0]!.id] : values.spaceIds);
      const finalPrimarySpaceId = values.primarySpaceId
        ?? (spaces?.length === 1 ? spaces[0]!.id : (undefined as unknown as number));
      await createMutation.mutateAsync({
        data: {
          vendorName: values.vendorName,
          businessType: values.businessType as typeof BusinessType[keyof typeof BusinessType],
          vendorType: values.vendorType as typeof VendorType[keyof typeof VendorType],
          gstin: values.gstin,
          pan: values.pan,
          city: values.city,
          state: values.state,
          officeAddress: values.officeAddress,
          status: values.status as typeof VendorStatus[keyof typeof VendorStatus] | undefined,
          cancelledChequeOnFile: values.cancelledChequeOnFile,
          tdsApplicable: values.tdsApplicable,
          emergencyContactAvailable: values.emergencyContactAvailable,
          spaceIds: finalSpaceIds,
          primarySpaceId: finalPrimarySpaceId,
          contacts: values.contacts?.map(c => ({
            role: c.role as "primary" | "secondary" | "escalation" | "other",
            name: c.name,
            mobile: c.mobile,
          })),
        }
      });
      toast({ title: "Vendor saved." });
      queryClient.invalidateQueries({ queryKey: getListInventoryVendorsQueryKey() });
      handleClose();
    } catch {
      toast({ title: "Something went wrong. Please try again.", variant: "destructive" });
    }
  }

  async function handleArchiveConfirm() {
    if (!archiveVendor) return;
    try {
      await updateMutation.mutateAsync({ id: archiveVendor.id, data: { status: "archived" } });
      toast({ title: `${venId(archiveVendor.id)} archived.` });
      queryClient.invalidateQueries({ queryKey: getListInventoryVendorsQueryKey() });
      setArchiveVendor(null);
    } catch {
      toast({ title: "Something went wrong. Please try again.", variant: "destructive" });
    }
  }

  return (
    <>
      <div className="mb-4 flex gap-2 items-center flex-wrap">
        <Input
          placeholder="Search vendors…"
          className="w-full sm:w-[220px]"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <Select value={filterStatus ?? "all"} onValueChange={(v) => setFilterStatus(v === "all" ? undefined : v)}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.values(VendorStatus).map((s) => (
              <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            Vendors{" "}
            <span className="text-muted-foreground font-normal text-sm">
              {vendors ? `(${vendors.length})` : ""}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Vendor Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>City</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(6)].map((__, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-24" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : vendors?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                    No vendors yet. Add your first vendor to start building the pricing catalog.
                  </TableCell>
                </TableRow>
              ) : (
                vendors?.map((vendor) => (
                  <TableRow key={vendor.id}>
                    <TableCell className="font-mono text-xs">{venId(vendor.id)}</TableCell>
                    <TableCell className="font-medium">{vendor.vendorName}</TableCell>
                    <TableCell className="text-sm">{vendor.vendorType.replace(/_/g, " ")}</TableCell>
                    <TableCell>
                      {vendor.status === "archived" ? (
                        <Badge variant="outline" className="text-muted-foreground">{vendor.status}</Badge>
                      ) : (
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${VENDOR_STATUS_COLORS[vendor.status] ?? "bg-secondary text-secondary-foreground"}`}>
                          {vendor.status.replace(/_/g, " ")}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{vendor.city ?? "—"}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => setViewVendorId(vendor.id)}>View</Button>
                      <Button size="sm" variant="ghost" onClick={() => openEditVendor(vendor)}>Edit</Button>
                      {vendor.status !== "archived" && (
                        <Button size="sm" variant="ghost" onClick={() => setArchiveVendor(vendor)}>Archive</Button>
                      )}
                      <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => setDeleteVendor(vendor)}>Delete</Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* View existing vendor sheet */}
      <Sheet open={viewVendorId !== null && !openCreate} onOpenChange={(o) => !o && setViewVendorId(null)}>
        <SheetContent side="right" className="w-[520px] p-0 flex flex-col">
          <SheetHeader className="p-6 pb-0">
            <SheetTitle>
              {viewVendorId ? `${venId(viewVendorId)} — Vendor Details` : "Vendor Details"}
            </SheetTitle>
          </SheetHeader>
          <VendorSheetContent vendorId={viewVendorId} onClose={() => setViewVendorId(null)} />
        </SheetContent>
      </Sheet>

      {/* Create / Edit vendor sheet (same form) */}
      <Sheet open={openCreate || editVendor !== null} onOpenChange={(o) => !o && handleClose()}>
        <SheetContent side="right" className="w-[520px] flex flex-col p-0">
          <SheetHeader className="p-6 pb-0">
            <SheetTitle>{editVendor ? `Edit ${venId(editVendor.id)} — ${editVendor.vendorName}` : "Add Vendor"}</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-hidden">
            <ScrollArea className="h-full">
              <form id="vendor-create-form" onSubmit={form.handleSubmit(onSubmit)} className="p-6 space-y-4">
                <div>
                  <p className="text-sm font-semibold text-foreground mb-3">Identity & Legal</p>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-semibold">Vendor Name <span className="text-destructive">*</span></label>
                      <Input {...form.register("vendorName")} />
                      {form.formState.errors.vendorName && <p className="text-xs text-destructive mt-1">{form.formState.errors.vendorName.message}</p>}
                    </div>
                    {/* Vendor Nature — quick Online/Offline/Service toggle that
                        sets a sensible default vendor_type. The detailed Vendor
                        Type dropdown below stays for finer choices (Quick_Commerce
                        vs Online_Marketplace; Regular vs One_Time). */}
                    <div>
                      <label className="text-xs font-semibold">Vendor Nature</label>
                      <VendorNatureToggle
                        value={form.watch("vendorType")}
                        onChange={(t) => form.setValue("vendorType", t)}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs font-semibold">Business Type <span className="text-destructive">*</span></label>
                        <Select defaultValue="Proprietorship" onValueChange={(v) => form.setValue("businessType", v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Object.values(BusinessType).map((t) => (
                              <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs font-semibold">Vendor Type <span className="text-destructive">*</span></label>
                        <Select value={form.watch("vendorType")} onValueChange={(v) => form.setValue("vendorType", v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Object.values(VendorType).map((t) => (
                              <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs font-semibold">GSTIN</label>
                        <Input {...form.register("gstin")} />
                      </div>
                      <div>
                        <label className="text-xs font-semibold">PAN</label>
                        <Input {...form.register("pan")} />
                      </div>
                    </div>
                  </div>
                </div>
                <Separator className="my-4" />
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <p className="text-sm font-semibold text-foreground">Contacts</p>
                    <Button type="button" size="sm" variant="outline" onClick={() => appendContact({ role: "primary", name: "", mobile: "" })}>
                      + Add Contact
                    </Button>
                  </div>
                  {contactFields.map((field, index) => (
                    <div key={field.id} className="grid grid-cols-3 gap-2 mb-2">
                      <Input placeholder="Name" {...form.register(`contacts.${index}.name`)} />
                      <Input placeholder="Mobile" {...form.register(`contacts.${index}.mobile`)} />
                      <Button type="button" size="sm" variant="ghost" className="text-destructive" onClick={() => removeContact(index)}>Remove</Button>
                    </div>
                  ))}
                </div>
                <Separator className="my-4" />
                <div>
                  <p className="text-sm font-semibold text-foreground mb-3">Addresses</p>
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs font-semibold">City</label>
                        <Input {...form.register("city")} />
                      </div>
                      <div>
                        <label className="text-xs font-semibold">State</label>
                        <Input {...form.register("state")} />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold">Office Address</label>
                      <Textarea {...form.register("officeAddress")} />
                    </div>
                  </div>
                </div>
                <Separator className="my-4" />
                <div>
                  <p className="text-sm font-semibold text-foreground mb-3">Status</p>
                  <Select defaultValue="prospect" onValueChange={(v) => form.setValue("status", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.values(VendorStatus).map((s) => (
                        <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {(spaces?.length ?? 0) > 1 ? (
                  <>
                    <Separator className="my-4" />
                    <div>
                      <p className="text-sm font-semibold text-foreground mb-3">Coverage (D-14)</p>
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs font-semibold">Spaces <span className="text-destructive">*</span></label>
                          <p className="text-xs text-muted-foreground mb-2">Which spaces does this vendor serve?</p>
                          <div className="space-y-2">
                            {spaces?.map((s) => {
                              const checked = (form.watch("spaceIds") ?? []).includes(s.id);
                              return (
                                <div key={s.id} className="flex items-center space-x-2">
                                  <Checkbox
                                    checked={checked}
                                    onCheckedChange={(v) => {
                                      const current = (form.getValues("spaceIds") as number[]) ?? [];
                                      const next = v ? [...current, s.id] : current.filter((x) => x !== s.id);
                                      form.setValue("spaceIds", next, { shouldValidate: true });
                                      if (!v && form.getValues("primarySpaceId") === s.id) {
                                        form.setValue("primarySpaceId", next[0] ?? (undefined as unknown as number), { shouldValidate: true });
                                      }
                                    }}
                                  />
                                  <label className="text-sm">{s.name}</label>
                                </div>
                              );
                            })}
                          </div>
                          {form.formState.errors.spaceIds && <p className="text-xs text-destructive mt-1">{form.formState.errors.spaceIds.message as string}</p>}
                        </div>
                        <div>
                          <label className="text-xs font-semibold">Primary Space <span className="text-destructive">*</span></label>
                          {(() => {
                            const selectedIds = (form.watch("spaceIds") as number[]) ?? [];
                            const selectedSpaces = spaces?.filter((s) => selectedIds.includes(s.id)) ?? [];
                            if (selectedSpaces.length === 0) {
                              return <p className="text-xs text-muted-foreground italic">Select at least one space above first.</p>;
                            }
                            return (
                              <RadioGroup
                                value={String(form.watch("primarySpaceId") ?? "")}
                                onValueChange={(v) => form.setValue("primarySpaceId", Number(v), { shouldValidate: true })}
                                className="space-y-2"
                              >
                                {selectedSpaces.map((s) => (
                                  <div key={s.id} className="flex items-center space-x-2">
                                    <RadioGroupItem value={String(s.id)} id={`primary-${s.id}`} />
                                    <label htmlFor={`primary-${s.id}`} className="text-sm font-normal">{s.name}</label>
                                  </div>
                                ))}
                              </RadioGroup>
                            );
                          })()}
                          {form.formState.errors.primarySpaceId && <p className="text-xs text-destructive mt-1">{form.formState.errors.primarySpaceId.message as string}</p>}
                        </div>
                      </div>
                    </div>
                  </>
                ) : null}
              </form>
            </ScrollArea>
          </div>
          <SheetFooter className="p-6 border-t">
            <Button type="button" variant="outline" onClick={handleClose}>Discard changes</Button>
            <Button type="submit" form="vendor-create-form" disabled={createMutation.isPending || updateMutation.isPending}>
              {editVendor
                ? (updateMutation.isPending ? "Saving…" : "Save Changes")
                : (createMutation.isPending ? "Saving…" : "Save Vendor")}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog open={archiveVendor !== null} onOpenChange={(o) => !o && setArchiveVendor(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Archive {archiveVendor ? venId(archiveVendor.id) : ""} — {archiveVendor?.vendorName}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Archiving removes this vendor from active selection. Existing pricing rows and stock history are preserved. This can be reversed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleArchiveConfirm}
            >
              Archive Vendor
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteVendor !== null} onOpenChange={(o) => !o && setDeleteVendor(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {deleteVendor ? venId(deleteVendor.id) : ""} — {deleteVendor?.vendorName}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the vendor. It can&rsquo;t be undone. A vendor with linked
              stock-ins or pricing can&rsquo;t be deleted — use <b>Archive</b> instead to keep that history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete Vendor"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
